import React, { useState, useEffect, useCallback } from "react";
import { Icon } from "@iconify/react";

import { Modal, Button, Form } from "react-bootstrap";
import { createErpSocket } from "../utils/socketIo";
import Select from "react-select";

import toast from "react-hot-toast";
import { getUserData } from "./const";
import Pagination from "../components/Pagination";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import {
  listItemCategories,
  createItemCategory,
  updateItemCategory,
  listCoreProducts,
  createCoreProduct,
} from "../erpClient";
import { parseApiPagination, extractListRowsFromResponse } from "../utils/parseApiPagination";
import { hasPermission, PERMISSIONS } from "../utils/permissions";

function ItemCategoryTable() {
  // const navigate = useNavigate();
  const { buttonClass, tableClass, cardClass } = useButtonTableStyle();
  const canCoreProducts = hasPermission(PERMISSIONS.PRODUCT_ASSORTING);
  const [itemCategories, setItemCategories] = useState([]);
  const [productAssortingOptions, setProductAssortingOptions] = useState([]);
  const [productAssortingLookup, setProductAssortingLookup] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showProductAssortingModal, setShowProductAssortingModal] =
    useState(false);

  // Add Item Category form states
  const [categoryName, setCategoryName] = useState("");
  const [shortCode, setShortCode] = useState("");
  const [status, setStatus] = useState("active");

  // Add Product Assorting form states
  const [assortingCategoryName, setAssortingCategoryName] = useState("");
  const [selectedProductAssortingId, setSelectedProductAssortingId] =
    useState("");
  const [assortingOptionsLoading, setAssortingOptionsLoading] = useState(false);
  const [socket, setSocket] = useState(null);

  const mapCategoryRows = (categories) =>
    categories.map((category) => ({
      id: category.id,
      categoryName: category.name || "",
      shortCode: Array.isArray(category.short_code)
        ? category.short_code.join(", ")
        : category.short_code || "",
      status:
        category.status !== null && category.status !== undefined
          ? String(category.status)
          : "1",
      productAssortingId: String(
        category.core_product_id ??
        category.core_product?.id ??
        category.product_assorting_id ??
        category.product_assorting?.id ??
        "",
      ),
      productAssortingName:
        category.core_product_name ??
        category.core_product?.name ??
        category.product_assorting_name ??
        category.product_assorting?.name ??
        "",
    }));

  const fetchItemCategories = useCallback(
    async (page = 1, limit = 10, search = "") => {
      setIsLoading(true);
      try {
        const response = await listItemCategories({
          search: search.trim(),
          page,
          limit,
        });

        const categories = extractListRowsFromResponse(response, [
          "item_categories",
          "categories",
        ]);

        if (Array.isArray(categories)) {
          const fetchedItems = mapCategoryRows(categories);
          fetchedItems.sort((a, b) => b.id - a.id);
          setItemCategories(fetchedItems);
          setTotalCount(parseApiPagination(response, fetchedItems.length).total);
        } else {
          setItemCategories([]);
          setTotalCount(0);
          toast.error(response?.message || "Invalid data format received");
        }
      } catch (error) {
        console.error("Error fetching item categories:", error);
        toast.error(
          error.response?.data?.message || "Failed to fetch item categories",
        );
        setItemCategories([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const getProductAssortingDisplayName = (category) => {
    if (!category) return "";
    if (
      category.productAssortingId &&
      productAssortingLookup[category.productAssortingId]
    ) {
      return productAssortingLookup[category.productAssortingId];
    }
    return category.productAssortingName || "";
  };

  const fetchProductAssortings = async () => {
    setAssortingOptionsLoading(true);
    try {
      const response = await listCoreProducts();
      let rawOptions = [];
      if (response?.success) {
        const data = response?.data;
        if (Array.isArray(data?.core_products)) rawOptions = data.core_products;
        else if (Array.isArray(data?.items)) rawOptions = data.items;
        else if (Array.isArray(data)) rawOptions = data;
      }

      // console.log("Raw options before processing:", rawOptions);

      const normalizedOptions = rawOptions
        .map((option) => {
          if (!option) return null;
          if (typeof option === "string") {
            return { id: option, name: option };
          }

          const name =
            option.category_name ??
            option.name ??
            option.assorting_name ??
            option.product_assorting ??
            option.value ??
            option.title ??
            option.category ??
            option.label ??
            "";

          if (!name) return null;

          const id =
            option.id ??
            option.ID ??
            option.value ??
            option.uuid ??
            option.key ??
            name;

          return { id: String(id), name: String(name) };
        })
        .filter(Boolean);

      // console.log("Normalized options:", normalizedOptions);
      setProductAssortingOptions(normalizedOptions);
      const lookup = {};
      normalizedOptions.forEach((option) => {
        lookup[option.id] = option.name;
      });
      // console.log("Product Assorting Lookup:", lookup);
      setProductAssortingLookup(lookup);
    } catch (error) {
      console.error("Error fetching product assortings:", error);
    } finally {
      setAssortingOptionsLoading(false);
    }
  };

  const ensureProductAssortingsLoaded = () => {
    if (productAssortingOptions.length > 0 || assortingOptionsLoading) return;
    void fetchProductAssortings();
  };

  const updateItemCategories = async (updatedCategory) => {
    try {
      const response = await updateItemCategory({
        id: updatedCategory.id,
        name: updatedCategory.categoryName,
        short_code: updatedCategory.shortCode,
        status: Number(updatedCategory.status) || 0,
        core_product_id: updatedCategory.productAssortingId
          ? Number(updatedCategory.productAssortingId)
          : null,
      });
      if (response?.success) {
        toast.success("Item category updated successfully");
        setItemCategories((prev) =>
          prev
            .map((c) =>
              c.id === updatedCategory.id ? { ...c, ...updatedCategory } : c,
            )
            .sort((a, b) => b.id - a.id),
        );
      }
    } catch (error) {
      console.error("Error updating item category:", error);
      toast.error(
        error.response?.data?.message || "Failed to update item category",
      );
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        fetchItemCategories(currentPage, itemsPerPage, searchQuery);
      },
      searchQuery ? 200 : 0,
    );
    return () => clearTimeout(timeoutId);
  }, [currentPage, itemsPerPage, searchQuery, fetchItemCategories]);

  // Listen for API URL changes and refetch data
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log(
        "API URL changed in ItemCategoryTable, refetching categories...",
        event.detail,
      );
      setCurrentPage(1);
      fetchItemCategories(1, itemsPerPage, searchQuery);
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, [itemsPerPage, searchQuery, fetchItemCategories]);

  // Socket.IO listener for real-time updates
  useEffect(() => {
    const newSocket = createErpSocket();
    setSocket(newSocket);

    // Connection event logging
    newSocket.on("connect", () => {
      // console.log(
      //   "ItemCategoryTable: Connected to socket server",
      //   newSocket.id,
      // );
    });

    newSocket.on("disconnect", (reason) => {
      // console.log(
      //   "ItemCategoryTable: Disconnected from socket server:",
      //   reason,
      // );
    });

    newSocket.on("connect_error", (error) => {
      console.error("ItemCategoryTable: Socket connection error:", error);
    });

    newSocket.on("item_category_updated", (data) => {
      const incoming = data?.item_category || data?.category;
      if (!incoming) return;
      setItemCategories((prev) =>
        prev.map((cat) =>
          cat.id === incoming.id
            ? {
              ...cat,
              categoryName: incoming.name || cat.categoryName,
              shortCode: Array.isArray(incoming.short_code)
                ? incoming.short_code.join(", ")
                : incoming.short_code || cat.shortCode,
              status:
                incoming.status !== null && incoming.status !== undefined
                  ? String(incoming.status)
                  : cat.status,
              productAssortingId: String(
                incoming.core_product_id ??
                incoming.core_product?.id ??
                incoming.product_assorting_id ??
                incoming.product_assorting?.id ??
                cat.productAssortingId ??
                "",
              ),
              productAssortingName:
                incoming.core_product_name ??
                incoming.core_product?.name ??
                incoming.product_assorting_name ??
                incoming.product_assorting?.name ??
                cat.productAssortingName ??
                "",
            }
            : cat,
        ),
      );
    });

    newSocket.on("item_category_created", (data) => {
      const cat = data?.item_category || data?.category;
      if (!cat) return;
      const newCategory = {
        id: cat.id,
        categoryName: cat.name || "",
        shortCode: Array.isArray(cat.short_code)
          ? cat.short_code.join(", ")
          : cat.short_code || "",
        status:
          cat.status !== null && cat.status !== undefined
            ? String(cat.status)
            : "1",
        productAssortingId: String(
          cat.core_product_id ??
          cat.core_product?.id ??
          cat.product_assorting_id ??
          cat.product_assorting?.id ??
          "",
        ),
        productAssortingName:
          cat.core_product_name ??
          cat.core_product?.name ??
          cat.product_assorting_name ??
          cat.product_assorting?.name ??
          "",
      };

      setItemCategories((prev) => {
        // Check if category already exists (avoid duplicates)
        const exists = prev.some((cat) => cat.id === newCategory.id);
        if (exists) {
          return prev.map((cat) =>
            cat.id === newCategory.id ? newCategory : cat,
          );
        }
        // Add new category at the beginning (newest first)
        return [newCategory, ...prev].sort((a, b) => b.id - a.id);
      });
    });
    newSocket.on("core_product_created", (data) => {
      const cp = data?.core_product;
      if (!cp) return;
      const name = cp.name || "";
      if (!name) return;
      const id = String(cp.id ?? "");
      if (!id) return;
      const newProduct = { id, name };

      // Update both options and lookup
      setProductAssortingOptions((prev) => {
        // Check if already exists to avoid duplicates
        const exists = prev.some((p) => p.id === id);
        if (exists) {
          return prev.map((p) => (p.id === id ? newProduct : p));
        }
        return [newProduct, ...prev];
      });

      // Update lookup object
      setProductAssortingLookup((prev) => ({
        ...prev,
        [id]: name,
      }));
    });

    return () => {
      newSocket.off("item_category_updated");
      newSocket.off("item_category_created");
      newSocket.off("core_product_created");
      newSocket.disconnect();
    };
  }, []);

  const handleEditClick = (category) => {
    // Normalize status to ensure radio buttons work correctly
    // Convert status to string and ensure it's either "0" or "1"
    let normalizedStatus = String(category.status || "1");
    if (normalizedStatus !== "0" && normalizedStatus !== "1") {
      normalizedStatus = "1"; // Default to Active if invalid
    }

    setSelectedCategory({
      ...category,
      status: normalizedStatus,
      productAssortingId: category.productAssortingId || "",
      productAssortingName:
        category.productAssortingName ||
        (category.productAssortingId
          ? productAssortingLookup[category.productAssortingId] || ""
          : ""),
    });
    setShowEditModal(true);
    ensureProductAssortingsLoaded();
  };

  const handleSaveChanges = () => {
    updateItemCategories(selectedCategory);
    setShowEditModal(false);
  };

  // Status filter on current server page
  const filteredCategories = itemCategories.filter((category) => {
    const matchesStatus =
      statusFilter === "" ||
      (statusFilter === "Active" &&
        (category.status === "1" || category.status === 1)) ||
      (statusFilter === "Inactive" &&
        category.status !== "1" &&
        category.status !== 1) ||
      category.status === "";

    return matchesStatus;
  });

  const handlePageChange = (newPage) => {
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const displayedCategories = filteredCategories;

  // Generate Short Code based on Category Name with improved logic
  const generateShortCode = (name) => {
    if (!name) return "";

    const words = name.split(" ").filter((word) => word.trim() !== "");

    // If just one word, take first 3 letters (or all if word is shorter)
    if (words.length === 1) {
      const word = words[0];
      return word.length <= 3
        ? word.toUpperCase()
        : word.substring(0, 3).toUpperCase();
    }

    // If multiple words, take first letter of each word
    return words
      .filter((word) => word && !word.includes("-"))
      .map((word) => word.charAt(0).toUpperCase())
      .join("");
  };

  // Handle Category Name Change
  const handleCategoryNameChange = (e) => {
    const name = e.target.value;
    setCategoryName(name);
    setShortCode(generateShortCode(name));
  };

  // Handle Add Item Category Form Submission
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await createItemCategory({
        name: categoryName,
        short_code: shortCode.split(",").map((code) => code.trim()),
        status: status === "active" ? 1 : 0,
        core_product_id: selectedProductAssortingId
          ? Number(selectedProductAssortingId)
          : null,
      });

      if (response?.success) {
        toast.success(response.message || "Item category added successfully");

        setShowAddModal(false);

        // Reset form fields
        setCategoryName("");
        setShortCode("");
        setStatus("active");
        setSelectedProductAssortingId("");

        const data = response.data?.item_category || response.data || {};
        if (data?.id !== undefined && data?.id !== null) {
          const next = {
            id: data.id,
            categoryName: data.name ?? categoryName ?? "",
            shortCode: Array.isArray(data.short_code)
              ? data.short_code.join(", ")
              : Array.isArray(shortCode)
                ? shortCode.join(", ")
                : String(shortCode || ""),
            status: String(data.status ?? (status === "active" ? 1 : 0)),
            productAssortingId: String(
              data.core_product_id ??
              data.core_product?.id ??
              selectedProductAssortingId ??
              "",
            ),
            productAssortingName:
              data.core_product_name ??
              data.core_product?.name ??
              (selectedProductAssortingId
                ? productAssortingLookup[selectedProductAssortingId] || ""
                : ""),
          };
          setItemCategories((prev) => {
            const exists = prev.some((c) => c.id === next.id);
            const merged = exists
              ? prev.map((c) => (c.id === next.id ? { ...c, ...next } : c))
              : [next, ...prev];
            return merged.sort((a, b) => b.id - a.id);
          });
        } else {
          fetchItemCategories(currentPage, itemsPerPage, searchQuery);
        }
      } else {
        toast.error(response?.message || "Failed to add item category");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(
        error.response?.data?.message || "Failed to add item category",
      );
    }
  };

  // Reset add form fields when modal closes
  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setCategoryName("");
    setShortCode("");
    setStatus("active");
    setSelectedProductAssortingId("");
  };

  // Handle Product Assorting Modal Close
  const handleCloseProductAssortingModal = () => {
    setShowProductAssortingModal(false);
    setAssortingCategoryName("");
  };

  // Handle Product Assorting Save
  const handleProductAssortingSave = async () => {
    if (!canCoreProducts) {
      toast.error("You do not have permission to add product assorting.");
      return;
    }
    if (!assortingCategoryName.trim()) {
      toast.error("Please enter a category name");
      return;
    }

    try {
      const response = await createCoreProduct({
        name: assortingCategoryName.trim(),
        status: 1,
      });

      if (response?.success) {
        toast.success(
          response.message || "Product Assorting created successfully",
        );
        handleCloseProductAssortingModal();
        // Update options from response if possible; otherwise refetch.
        const cp = response.data?.core_product || response.data || null;
        if (cp && cp.id !== undefined && cp.id !== null) {
          const newProduct = {
            id: String(cp.id),
            name: cp.name || assortingCategoryName.trim(),
          };
          setProductAssortingOptions((prev) => {
            const exists = prev.some((p) => p.id === newProduct.id);
            return exists
              ? prev.map((p) => (p.id === newProduct.id ? newProduct : p))
              : [newProduct, ...prev];
          });
          setProductAssortingLookup((prev) => ({
            ...prev,
            [newProduct.id]: newProduct.name,
          }));
        } else {
          fetchProductAssortings();
        }
      } else {
        toast.error(response?.message || "Failed to save product assorting");
      }
    } catch (error) {
      console.error("Error saving product assorting:", error);
      toast.error(
        error.response?.data?.message || "Failed to save product assorting",
      );
    }
  };

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div
          className={`card w-100 position-relative overflow-hidden ${cardClass}`}
        >
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Item Category Table</h4>
            <div className="d-flex gap-2">
              <button
                type="button"
                className={`btn btn-primary ${buttonClass}`}
                disabled={!canCoreProducts}
                aria-disabled={!canCoreProducts}
                title={
                  canCoreProducts
                    ? "Add product assorting (core product)"
                    : "You need Core products (CORE_PRODUCTS) permission"
                }
                onClick={() => {
                  if (!canCoreProducts) return;
                  setShowProductAssortingModal(true);
                }}
              >
                Add Product Assorting
              </button>
              <button
                className={`btn btn-success ${buttonClass}`}
                onClick={() => {
                  setShowAddModal(true);
                }}
              >
                Add Item Category
              </button>
            </div>
          </div>

          <div className="card-body p-4">
            {/* Search and Filter Section */}
            <div className="d-flex justify-content-between mb-3">
              <input
                type="text"
                className={`form-control ${buttonClass}`}
                style={{ maxWidth: "260px" }}
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <select
                className={`form-select w-auto ms-3 ${buttonClass}`}
                style={{ maxWidth: "200px" }}
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            {/* Table */}
            <div className={`table-responsive mb-4 border ${cardClass}`}>
              <table
                className={`table text-nowrap mb-0 align-middle ${tableClass}`}
              >
                <thead className="text-dark fs-4">
                  <tr>
                    <th>Category</th>
                    {/* <th>Short Code</th> */}
                    <th>Product Assorting</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="4" className="text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : displayedCategories.length > 0 ? (
                    displayedCategories.map((category) => (
                      <tr key={category.id}>
                        <td>{category.categoryName || "N/A"}</td>
                        {/* <td>{category.shortCode || "N/A"}</td> */}
                        <td>
                          {getProductAssortingDisplayName(category) || "N/A"}
                        </td>
                        <td>
                          <span
                            className={`badge ${category.status === "1" || category.status === 1
                              ? "bg-success-subtle text-success"
                              : "bg-danger-subtle text-danger"
                              }`}
                          >
                            {category.status === "1" || category.status === 1
                              ? "Active"
                              : "Inactive"}
                          </span>
                        </td>
                        <td>
                          <button
                            className={`btn btn-primary btn-sm ${buttonClass}`}
                            onClick={() => handleEditClick(category)}
                          >
                            <Icon
                              icon="solar:pen-bold"
                              width="16"
                              height="16"
                            />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center">
                        No categories found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <Pagination
              totalItems={totalCount}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              itemsPerPageOptions={[10, 20, 30, 50, 100]}
            />
          </div>
        </div>
      </div>

      {/* Edit Item Category Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
        contentClassName={cardClass}
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Item Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCategory && (
            <Form>
              <div className="row">
                <div className="mb-3 col-md-12">
                  <Form.Label>Product Assorting</Form.Label>
                  <Select
                    value={
                      selectedCategory?.productAssortingId
                        ? {
                          value: selectedCategory.productAssortingId,
                          label:
                            selectedCategory.productAssortingName ||
                            productAssortingLookup[
                            selectedCategory.productAssortingId
                            ] ||
                            selectedCategory.productAssortingId,
                        }
                        : null
                    }
                    onChange={(selectedOption) => {
                      // console.log(
                      //   "Edit modal - Selected option:",
                      //   selectedOption
                      // );
                      const value = selectedOption ? selectedOption.value : "";
                      setSelectedCategory({
                        ...selectedCategory,
                        productAssortingId: value,
                        productAssortingName: selectedOption
                          ? selectedOption.label
                          : "",
                      });
                    }}
                    options={productAssortingOptions.map((option) => ({
                      value: option.id,
                      label: option.name,
                    }))}
                    placeholder="Select Product Assorting"
                    isClearable
                    isSearchable
                    onMenuOpen={ensureProductAssortingsLoaded}
                    menuPortalTarget={document.body}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (base) => ({
                        ...base,
                        minHeight: "38px",
                        fontSize: "14px",
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                    }}
                    noOptionsMessage={() =>
                      assortingOptionsLoading
                        ? "Loading options..."
                        : "No options found"
                    }
                  />
                </div>
              </div>
              <div className="row">
                {/* Category Name */}
                <div className="mb-3 col-md-12">
                  <Form.Label>Category Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedCategory.categoryName}
                    className={buttonClass}
                    onChange={(e) =>
                      setSelectedCategory({
                        ...selectedCategory,
                        categoryName: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
              <div className="row">
                {/* Short Code */}
                <div className="mb-3 col-md-12">
                  <Form.Label>Short Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedCategory.shortCode}
                    className={buttonClass}
                    onChange={(e) =>
                      setSelectedCategory({
                        ...selectedCategory,
                        shortCode: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div className="row">
                {/* Item Status */}
                <div className="mb-3 col-md-12">
                  <Form.Label>Item Status</Form.Label>
                  <div className="d-flex gap-3">
                    <Form.Check
                      type="radio"
                      label="Active"
                      name="status"
                      value="1"
                      checked={String(selectedCategory.status) === "1"}
                      onChange={(e) =>
                        setSelectedCategory({
                          ...selectedCategory,
                          status: "1",
                        })
                      }
                    />
                    <Form.Check
                      type="radio"
                      label="Inactive"
                      name="status"
                      value="0"
                      checked={String(selectedCategory.status) === "0"}
                      onChange={(e) =>
                        setSelectedCategory({
                          ...selectedCategory,
                          status: "0",
                        })
                      }
                    />
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            className={buttonClass}
            onClick={handleSaveChanges}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Item Category Modal */}
      <Modal
        show={showAddModal}
        onHide={handleCloseAddModal}
        centered
        contentClassName={cardClass}
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Item Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form noValidate="" onSubmit={handleAddSubmit}>
            <div className="row">
              <div className="mb-3 col-md-12">
                <label className="form-label">Product Assorting <span className="text-danger">*</span></label>
                <Select
                  value={
                    selectedProductAssortingId
                      ? {
                        value: selectedProductAssortingId,
                        label:
                          productAssortingLookup[
                          selectedProductAssortingId
                          ] || selectedProductAssortingId,
                      }
                      : null
                  }
                  onChange={(selectedOption) => {
                    console.log("Selected option:", selectedOption);
                    setSelectedProductAssortingId(
                      selectedOption ? selectedOption.value : "",
                    );
                  }}
                  options={productAssortingOptions.map((option) => {
                    // console.log("Mapping option:", option);
                    return {
                      value: option.id,
                      label: option.name,
                    };
                  })}
                  placeholder="Select Product Assorting"
                  isClearable
                  isSearchable
                  onMenuOpen={ensureProductAssortingsLoaded}
                  menuPortalTarget={document.body}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    control: (base) => ({
                      ...base,
                      minHeight: "38px",
                      fontSize: "14px",
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                  }}
                  noOptionsMessage={() =>
                    assortingOptionsLoading
                      ? "Loading options..."
                      : "No options found"
                  }
                />
              </div>
            </div>

            <div className="row">
              {/* Category Name */}
              <div className="mb-3 col-md-12 ">
                <label className="form-label">
                  Category Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${buttonClass}`}
                  required
                  placeholder="Enter Category Name"
                  value={categoryName}
                  onChange={handleCategoryNameChange}
                />
              </div>
            </div>
            <div className="row">
              {/* Short Code */}
              <div className="mb-3 col-md-12">
                <label className="form-label">
                  Short Code <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${buttonClass}`}
                  required
                  placeholder="Enter Short Code"
                  value={shortCode}
                  onChange={(e) => setShortCode(e.target.value)}
                />
              </div>
            </div>

            {/* Status */}
            {/* <div className="row">
              <div className="mb-3 col-md-6">
                <label className="form-label">
                  Item Status <span className="text-danger">*</span>
                </label>
                <div className="d-flex gap-3">
                  <div className="form-check">
                    <input
                      type="radio"
                      name="status"
                      className="form-check-input"
                      value="active"
                      checked={status === "active"}
                      onChange={(e) => setStatus(e.target.value)}
                    />
                    <label className="form-check-label">Active</label>
                  </div>
                  <div className="form-check">
                    <input
                      type="radio"
                      name="status"
                      className="form-check-input"
                      value="inactive"
                      checked={status === "inactive"}
                      onChange={(e) => setStatus(e.target.value)}
                    />
                    <label className="form-check-label">Inactive</label>
                  </div>
                </div>
              </div>
            </div> */}

            {/* Submit Button */}
            <div className="d-flex flex-wrap gap-3 justify-content-end">
              <button
                type="submit"
                className={`btn btn-primary ${buttonClass}`}
              >
                Submit
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      {/* Add Product Assorting Modal */}
      <Modal
        show={showProductAssortingModal}
        onHide={handleCloseProductAssortingModal}
        centered
        contentClassName={cardClass}
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Product Assorting</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            {/* Category Name */}
            <div className="mb-3 col-md-12">
              <Form.Label>Category Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Category Name"
                value={assortingCategoryName}
                className={buttonClass}
                onChange={(e) => setAssortingCategoryName(e.target.value)}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="justify-content-end">
          <Button
            variant="primary"
            className={buttonClass}
            onClick={handleProductAssortingSave}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ItemCategoryTable;
